/**
 * ADDON BUNDLING SYSTEM
 * Scope: Block-scoped to prevent re-declaration errors in modals/drawers.
 */
{
  /**
   * 1. UTILITY: GET ACTIVE VALUE
   * Reads the text from the custom select button for price calculations.
   */
  const getActiveAddonValue = (block) => {
    const customBtn = block.querySelector('.custom-select__btn span');
    if (customBtn) {
      const text = customBtn.textContent.trim();
      return (text === "" || text.includes('Select')) ? "Not Required" : text;
    }
    const nativeSelect = block.querySelector('select[name^="addon_proxy_input_"]');
    return nativeSelect ? nativeSelect.value : "Not Required";
  };

  /**
   * 2. GLOBAL PRICE UPDATER
   * Dynamically calculates the total price based on addon selections.
   */
  window.updateCombinedPrice = (scope = document) => {
    const priceWrapper = scope.querySelector('.js-main-price-wrapper');
    if (!priceWrapper) return;

    let totalCents = parseInt(priceWrapper.getAttribute('data-base-price'));
    if (isNaN(totalCents)) return;

    const addonBlocks = scope.querySelectorAll('.addon-bundle-container');

    addonBlocks.forEach(block => {
      const val = getActiveAddonValue(block);
      // Regex to find price in strings like "(+£64.00)"
      const priceMatch = val.match(/\(\+[^0-9]*([\d,.]+)\)/);
      
      if (priceMatch && priceMatch[1]) {
        const priceValue = parseFloat(priceMatch[1].replace(/,/g, ''));
        totalCents += Math.round(priceValue * 100);
      }
    });

    const formatter = new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    });

    const formattedPrice = formatter.format(totalCents / 100);

    // Update the main product price display
    const mainPriceValue = priceWrapper.querySelector('.js-value');
    if (mainPriceValue) {
      mainPriceValue.textContent = formattedPrice;
    }

    // Update Shop Pay / Installment previews if they exist
    const shopPayTerms = scope.querySelector('shopify-payment-terms');
    if (shopPayTerms && shopPayTerms.shadowRoot) {
      const boldAmount = shopPayTerms.shadowRoot.querySelector('.font-bold');
      if (boldAmount) {
        boldAmount.textContent = formatter.format((totalCents / 100) / 3);
      }
    }
  };

  /**
   * 3. BUNDLE PROCESSING LOGIC
   * Intercepts "Add to Cart" and adds selected addon variants via AJAX.
   */
  const processAddonBundling = function(e) {
    const atcButton = e.target.closest('[name="add"]');
    
    // Safety checks: ensure button is an ATC button and not currently bypassing the bundle
    if (!atcButton || atcButton.dataset.bundleBypass === "true") return;

    const productContainer = atcButton.closest('.js-product') || atcButton.closest('.product-info') || document;
    const addonBlocks = productContainer.querySelectorAll('.addon-bundle-container');
    
    // If no addons are present, let the theme handle the click normally
    if (addonBlocks.length === 0) return;

    const form = atcButton.closest('form');
    if (!form) return;

    const itemsToAdd = [];
    const unitQty = parseInt(form.elements['quantity']?.value || 1);
    const productTitle = productContainer.querySelector('.js-bundle-product-title')?.value || 
                         productContainer.querySelector('.product-title')?.textContent.trim() || 'Product';

    addonBlocks.forEach((block) => {
      // Direct lookup: Find the <li> that the theme's JS has marked as selected
      const selectedLi = block.querySelector('.custom-select__option[aria-selected="true"]');
      const multiplier = parseInt(block.querySelector('.js-addon-multiplier')?.value || 1);

      if (selectedLi && selectedLi.dataset.variantId) {
        const variantId = selectedLi.dataset.variantId;

        itemsToAdd.push({
          id: parseInt(variantId),
          quantity: unitQty * multiplier,
          properties: { 
            '_parent_unit': productTitle,
            '_bundle_slot': block.dataset.addonIndex // Distinguishes same variant in different slots
          }
        });
      }
    });

    // If there are addons to add, intercept the original click
    if (itemsToAdd.length > 0) {
      e.preventDefault();
      e.stopImmediatePropagation();

      itemsToAdd.reverse();

      atcButton.classList.add('is-loading');
      atcButton.setAttribute('aria-disabled', 'true');

      // Add all addons to the cart in one request
      fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest' 
        },
        body: JSON.stringify({ items: itemsToAdd })
      })
      .then(async (res) => {
        if (!res.ok) throw new Error('Addon addition failed');
        
        // Success: set bypass flag and re-trigger click to add the main product
        atcButton.dataset.bundleBypass = "true";
        atcButton.classList.remove('is-loading');
        atcButton.removeAttribute('aria-disabled');
        atcButton.click(); 
      })
      .catch((err) => {
        console.error('Bundle Error:', err);
        // Fallback: still try to add the main product if addons fail
        atcButton.dataset.bundleBypass = "true";
        atcButton.click();
      });
    }
  };

  /**
   * 4. EVENT LISTENERS
   */
  
  // Listen for changes on the proxy selects (native)
  document.addEventListener('change', (e) => {
    if (e.target.name && e.target.name.startsWith('addon_proxy_input_')) {
      const scope = e.target.closest('.js-product') || e.target.closest('.product-info') || document;
      window.updateCombinedPrice(scope);
    }
  });

  // Support for Enterprise theme's custom-select event
  document.addEventListener('custom-select:change', (e) => {
     const scope = e.target.closest('.js-product') || e.target.closest('.product-info') || document;
     window.updateCombinedPrice(scope);
  });

  // Intercept the Add to Cart click in the capture phase
  document.addEventListener('click', processAddonBundling, true);

  // Hook for Quick Add drawers to initialize price
  document.addEventListener('quickadd:loaded', (e) => {
    window.updateCombinedPrice(e.target);
  });

  /**
   * 5. INITIALIZATION
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => window.updateCombinedPrice());
  } else {
    window.updateCombinedPrice();
  }

  // Helper to reset the bypass flag if the user stays on the page (e.g. AJAX cart)
  const resetBundleFlag = () => {
    document.querySelectorAll('[name="add"]').forEach(btn => btn.dataset.bundleBypass = "false");
  };
  document.addEventListener('on:cart:add', resetBundleFlag);
  document.addEventListener('cart:updated', resetBundleFlag);
}